import {
  ClassDetailsQuery_ActivityFragment,
  ClassDetailsSectionsQuery as ClassDetailsSectionsQueryType,
  ClassDetailsSectionsQueryVariables,
  SectionFilter,
} from "@outschool/gql-frontend-generated";
import { dayjs } from "@outschool/time";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useEsaWaitPeriod, useSession } from "@outschool/ui-auth";
import { useIsMobile } from "@outschool/ui-utils";
import React from "react";

import { DESKTOP_ROWS_PER_PAGE } from "../availableTimes/DesktopSectionTable";
import { MOBILE_ROWS_PER_PAGE } from "../availableTimes/MobileSectionTable";
import { ClassDetailsQuery_Section } from "../subcomponents/ClassDetailsQuery";

const ClassDetailsSections_Activity = gql`
  fragment ClassDetailsSections_Activity on Activity {
    uid
    is_ongoing_weekly
    paginatedFilteredSections(
      filter: $sectionFilter
      esaBlackoutLengthDays: $esaBlackoutLengthDays
      offset: $offset
      limit: $limit
    ) {
      totalCount
      data {
        ...ClassDetailsQuery_Section
        __typename
      }
    }
  }
  ${ClassDetailsQuery_Section}
`;

const ClassDetailsSectionsQuery = gql`
  query ClassDetailsSections(
    $uid: ID
    $slugId: String
    $sectionFilter: SectionFilter
    $esaBlackoutLengthDays: Int
    $offset: Int
    $limit: Int
    $startAfter: DateTime!
  ) {
    activity(uid: $uid, slugId: $slugId) {
      ...ClassDetailsSections_Activity
      __typename
    }
  }
  ${ClassDetailsSections_Activity}
`;

interface UseClassDetailsSectionsProps {
  isLoading: boolean;
  paginatedFilteredSections?: ClassDetailsSectionsQueryType["activity"]["paginatedFilteredSections"];
  fetchMore: any;
}

export default function useClassDetailsSections({
  activityWithoutSections,
  skip = false,
  limitOverride = undefined,
}: {
  activityWithoutSections?: ClassDetailsQuery_ActivityFragment;
  skip?: boolean;
  limitOverride?: number;
}): UseClassDetailsSectionsProps {
  const { isEsaSession } = useSession();
  const esaWaitPeriod = useEsaWaitPeriod();
  const esaBlackoutLengthDays = isEsaSession ? esaWaitPeriod : null;
  const sectionFilter = React.useMemo(() => {
    if (activityWithoutSections?.allowWaitlist) {
      return SectionFilter.BookableOrSoldOut;
    }
    return SectionFilter.Bookable;
  }, [activityWithoutSections?.allowWaitlist]);

  const isMobile = useIsMobile();

  const limit =
    limitOverride ?? (isMobile ? MOBILE_ROWS_PER_PAGE : DESKTOP_ROWS_PER_PAGE);

  const startAfter = React.useMemo(() => {
    /**
     * Using the start of the ISO week because we use the meetings array
     * to display what days of the week the class meets so want to prevent
     * accidentally showing the wrong meeting days in certain cases.
     * For example: a 1 week long class _after_ the first meeting has started
     */
    return dayjs().startOf("isoWeek");
  }, []);

  const args = {
    variables: {
      uid: activityWithoutSections?.uid,
      sectionFilter,
      esaBlackoutLengthDays: esaBlackoutLengthDays,
      offset: 0,
      limit,
      startAfter,
    },
    skip: skip || !activityWithoutSections?.uid,
    ssr: false,
  };

  const { data, loading, fetchMore } = useQueryWithPreviousData<
    ClassDetailsSectionsQueryType,
    ClassDetailsSectionsQueryVariables
  >(ClassDetailsSectionsQuery, args);

  const activity = data?.activity;

  return {
    isLoading: loading && !!activityWithoutSections?.uid,
    paginatedFilteredSections: activity?.paginatedFilteredSections,
    fetchMore,
  };
}
